/** The props that fill the ground between the buildings.
 *
 * Castle has five back-band structures drawn at two thirds size, so however
 * they are spread there is more lawn behind the town than town. Bare lawn is
 * what makes a screen read as sprites on a backdrop: every building sits alone
 * in its own moat and nothing ever overlaps anything. The map-object atlas
 * already ships pines, oaks, hedges, benches, barrels and flower beds drawn in
 * the same hand and lit from the same side, so the gaps get planted.
 *
 * Deterministic: every prop is chosen and jittered from a hash of its own
 * position, so two runs of the same town draw the same trees and the
 * screenshot harness sees one image.
 *
 * Pure: no DOM, no React, no kernel rule module.
 */

import type { TownSlot } from "./townSlots.js";
import { BAND_SCALE, TOWN_CANVAS_H, TOWN_CANVAS_W, drawnWidth } from "./townSlots.js";
import { hash01 } from "./hash01.js";

export interface Prop {
  /** Atlas key, already namespaced. */
  readonly art: string;
  /** Horizontal centre and ground line, canvas pixels. */
  readonly x: number;
  readonly y: number;
  readonly scale: number;
  /** Painter's order runs on this, exactly as it does for a building. */
  readonly band: TownSlot["band"];
  readonly flip: boolean;
  /**
   * True for the two foreground occluders and nothing else.
   *
   * They are the only props nearer than the front band, they are the only ones
   * the frame cuts, and Deepvault's is the one asset a critic called the weakest
   * in the set - so they need a hook of their own in the stylesheet.
   */
  readonly fg: boolean;
  /**
   * True for the props that are their own light source.
   *
   * A country's `--hs-prop-tint` exists to put a green pine into a snowfield
   * or a brown rock into a cavern, and it does it with a large hue-rotate.
   * Rotated 150 degrees for the graveyard, Gravewatch' campfires came back
   * CYAN - a cold fire in a crypt, which a viewer names without being asked -
   * and the same rotation is waiting for every other fire in the set. A flame
   * does not take its country's light, it throws it, so these are pulled out
   * of the tint chain entirely and are the one thing on the screen that
   * animates.
   */
  readonly lit: boolean;
}

/**
 * The props that are their own light source.
 *
 * Keyed by atlas art rather than by a flag on each call site, because the
 * promenade walks a list and a fire can arrive in it from any country's
 * `square`.
 *
 * `obs_ember_pile` is NOT one, and it used to be. It is a grey boulder with
 * lava cracks in it, not a flame: over its own opaque pixels it measures
 * (83,63,58), and what this class does to a sprite is exempt it from its
 * country's tint - Emberpit's is brightness(0.46) - and hang two warm blur
 * shadows off it. A boulder exempted from the only thing darkening the frame
 * and then haloed is what a critic measured on Emberpit's promenade: "pale
 * glowing granite spheres... L=117 against a plaza at L=49, haloed rather
 * than seated". A rock that has fire in its cracks still takes the light of
 * the place it is in.
 */
const LIT_ART: ReadonlySet<string> = new Set([
  "obj.pu_campfire", "obj.obs_watchfire", "obj.obs_lava_pool",
]);

export const isLit = (art: string): boolean => LIT_ART.has(art);

/** Deterministic 0..1 from an integer seed. */

const pick = <T,>(list: readonly T[], seed: number): T =>
  list[Math.min(list.length - 1, Math.floor(hash01(seed) * list.length))]!;

/**
 * A town's own number, from its faction id.
 *
 * Everything under here is deterministic from a position, which is what makes
 * the screenshot harness see one image - and it is also what made twelve towns
 * share one square, because the positions are one table. The faction goes into
 * the seed so the layer is still deterministic and no longer identical.
 */
function nameSeed(faction: string): number {
  let h = 2166136261;
  for (let i = 0; i < faction.length; i++) {
    h = Math.imul(h ^ faction.charCodeAt(i), 16777619);
  }
  return h >>> 0;
}

/**
 * What kind of country the town stands in.
 *
 * A screen is a painted backdrop plus this layer, and pine trees planted on
 * Deepvault's cavern floor or Emberpit's cinders look exactly as wrong as they
 * sound. The same key also tints the paving: warm limestone reads as a road on
 * Castle's lawn and as a stripe of daylight across a cave.
 */
export type GroundKind =
  | "grass" | "snow" | "ash" | "cave" | "grave" | "rough" | "swamp" | "desert"
  | "coast";

const GROUND_BY_FACTION: Readonly<Record<string, GroundKind>> = Object.freeze({
  sunspire: "grass", greenmantle: "grass", prismgate: "grass",
  skyreach: "snow", emberpit: "ash", gravewatch: "grave",
  deepvault: "cave", ironhorde: "rough", fenmire: "swamp", cogworks: "desert",
  // Saltreach is a tropical anchorage, not a meadow. On `grass` its back band was
  // planted with dark-green northern conifers and an oak, its promenade with
  // a flower ring, and its paving took the lawn's cool limestone - against a
  // backdrop of sand, palms and blue water. `coast` is the same layer with
  // the country changed under it.
  saltreach: "coast",
  // Blackwater is a drowned tidal flat: brackish water, dead reed beds and
  // half-sunk timber. Its own backdrop measures a treeline horizon at row
  // ~200 over water at luma 38, which is the swamp's picture exactly, so it
  // shares the swamp's planting rather than getting a fourteenth country of
  // its own.
  blackwater: "swamp",
  // Frostmark is SNOW, not grass. Its backdrop is a snowfield under snowcapped
  // mountains and every one of its structures ships with snow on its roof; on
  // grass it was planting green pines and warm limestone paving in it. The
  // mismatch was always there and the foreground occluder is what made it
  // impossible to miss - two 370px cypresses in full summer green standing in
  // a snowfield.
  frostmark: "snow",
});

export const groundKind = (faction: string): GroundKind =>
  GROUND_BY_FACTION[faction] ?? "grass";

/** Every country there is, so a test can walk them without a second list. */
export const GROUND_KINDS: readonly GroundKind[] = Object.freeze([
  "grass", "snow", "ash", "cave", "grave", "rough", "swamp", "desert", "coast",
]);

interface Flora {
  /** Tall enough to break the skyline, which is what the back band needs. */
  readonly trees: readonly string[];
  readonly scrub: readonly string[];
  readonly furniture: readonly string[];
  /**
   * What stands on the near promenade.
   *
   * The strip of ground between the front band's doors and the wooden frame is
   * where the player's eye rests, and a hundred and forty pixels of empty
   * paving there is the first thing three critics reported. A town square has a
   * well, a cart, stacked timber, a fire: things at a person's own scale, which
   * is also the only place on the screen where true scale can be read.
   */
  readonly square: readonly string[];
}

const FLORA: Readonly<Record<GroundKind, Flora>> = Object.freeze({
  grass: {
    trees: ["obj.obs_pine_cluster", "obj.obs_pine", "obj.obs_oak", "obj.obs_pine_cluster"],
    scrub: ["obj.obs_hazel_bush", "obj.obs_bush", "obj.obs_hedgerow", "obj.obs_flowers"],
    furniture: ["obj.obs_stone_bench", "obj.obs_flower_ring", "obj.bld_sea_barrel",
      "obj.obs_flowers", "obj.obs_milestone"],
    square: ["obj.bld_well", "obj.obs_broken_cart", "obj.obs_woodpile", "obj.obs_stone_bench",
      "obj.pu_campfire", "obj.obs_signpost", "obj.obs_flowers"],
  },
  snow: {
    // Three designs after the occluder's own is filtered out - see
    // `plantable`. Two would leave the treeline alternating between one pine
    // and one rock, which is the hedge again one band further back.
    // `obs_icicle_rock` is out of both back-band lists, and it is the snow's
    // instance of the fault the steppe's banded dome was: it is a dark rock
    // OVERHANG with icicles hanging off its underside, drawn to be seen from
    // below. Planted on Frostmark's treeline it drew at device (1240,200) as a
    // slab in the sky with a fringe under it and nothing beneath that -
    // exactly the read a critic gave the desert rock and the swamp logs. What
    // sits on a snowfield is a boulder with snow on top of it and a frozen
    // fall with a plunge pool at its foot; both have a visible base.
    trees: ["obj.obs_snowpine", "obj.obs_pine_cluster", "obj.obs_frozen_falls",
      "obj.obs_ice_spikes"],
    scrub: ["obj.obs_frost_bush", "obj.obs_snow_boulder", "obj.obs_snow_fence", "obj.obs_frost_bush"],
    furniture: ["obj.obs_milestone", "obj.obs_rocks", "obj.bld_sea_barrel", "obj.obs_frost_bush"],
    square: ["obj.bld_well", "obj.obs_woodpile", "obj.pu_campfire", "obj.obs_snow_fence",
      "obj.obs_snow_cairn", "obj.obs_signpost", "obj.obs_broken_cart", "obj.obs_woodpile"],
  },
  ash: {
    // `obs_rocks` is a pale neutral granite and it was in three of these four
    // lists: grey boulders scattered over Emberpit's black cinders, in the one
    // frame in the set whose whole palette is ember on soot. Everything here
    // is either burnt, molten or vitrified.
    trees: ["obj.obs_scorched_tree", "obj.obs_deadtree", "obj.obs_lavarock", "obj.obs_burnt_stump"],
    scrub: ["obj.obs_lavarock", "obj.obs_deadbush", "obj.obs_obsidian_shard", "obj.obs_lavacrack"],
    furniture: ["obj.obs_lavarock", "obj.obs_milestone", "obj.obs_obsidian_shard", "obj.obs_ash_mound"],
    // `obs_rubble_pile` is out of the promenade: over its own opaque pixels it
    // measures L=86 with a 95th percentile of 182 and R-B +6, a pale NEUTRAL
    // grey, and on Emberpit's plaza - ground luma 34 - it was the brightest
    // object in the lower third of the frame and a critic named it as such.
    // `obs_slag_heap` is L=47 and warm, which is what a thing beside a lava
    // channel is.
    square: ["obj.obs_ember_pile", "obj.pu_campfire", "obj.obs_bone_pile", "obj.obs_lavarock",
      "obj.obs_broken_cart", "obj.obs_obsidian_shard", "obj.obs_slag_heap",
      "obj.obs_ember_pile"],
  },
  cave: {
    // `obs_moss_rock` is a green boulder and the cavern's own tint is
    // `hue-rotate(214deg)`, which takes green to MAGENTA. It was in all four
    // of these lists, so Deepvault's floor carried a dozen hot-pink boulders in
    // a frame built on violet and blue. The rotation is right for the country
    // and the asset is wrong for it: what a cave floor has is stalagmites,
    // basalt and fungus, none of which is green to start with.
    trees: ["obj.obs_rock_arch", "obj.obs_basalt_column", "obj.obs_stalagmite", "obj.obs_cave_column"],
    scrub: ["obj.obs_cave_mushroom", "obj.obs_rocks", "obj.obs_stalagmite", "obj.obs_toadstool_ring"],
    furniture: ["obj.obs_rocks", "obj.obs_cave_mushroom", "obj.obs_milestone", "obj.bld_sea_barrel"],
    square: ["obj.obs_rubble_pile", "obj.obs_rocks", "obj.pu_campfire", "obj.obs_stalagmite", "obj.obs_woodpile", "obj.obs_milestone", "obj.obs_cave_mushroom", "obj.obs_rocks"],
  },
  grave: {
    trees: ["obj.obs_deadtree", "obj.obs_scorched_tree", "obj.obs_deadtree", "obj.obs_rock_arch"],
    scrub: ["obj.obs_gravestones", "obj.obs_deadbush", "obj.obs_gravestones", "obj.obs_rocks"],
    furniture: ["obj.obs_gravestones", "obj.obs_broken_statue", "obj.obs_milestone", "obj.obs_deadbush"],
    square: ["obj.obs_gravestones", "obj.obs_grave_cross", "obj.obs_bone_pile", "obj.obs_broken_statue", "obj.obs_rubble_pile", "obj.obs_milestone", "obj.pu_campfire", "obj.obs_deadbush"],
  },
  rough: {
    // Ironhorde stands on red hardpan under a warm sky and this list gave it
    // a spring-green oak, a spring-green hazel and three clumps of
    // `obs_tall_grass` - a bright temperate meadow green, which is the one
    // hue nothing else in that frame contains. What a steppe has is banded
    // rock, dead wood, scree and tumbleweed.
    // No `obs_mesa_rock` in this list either, and it is the same fault as the
    // swamp's logs one entry up. The mesa is a flat cream disc with a
    // horizontally banded skirt and no visible foot; planted on the painted
    // hillside behind the back band it read on the shipped shot as a pastry
    // hanging in the sky over Ironhorde at canvas row 266. A steppe's
    // uprights are eroded pillars and dead wood.
    // And no `obs_desert_rock` either, which is the same fault a third time
    // and was found in the shipped frame rather than reasoned about: a
    // 64x61 horizontally banded dome with one flat cap, an anti-aliased
    // silhouette and no foot. Planted on the treeline it drew at canvas
    // (371,259)-(481,323) on Ironhorde - clear of the ridge crest, with its
    // contact pool twenty rows below it touching nothing - and a critic read
    // it exactly as the mesa was read: "a banded cream dome... hovering, with
    // a soft blurred shadow smeared behind and below it". The whole banded
    // family is unplantable on a painted hillside; the pillar and the
    // stalagmite have a visible base and a vertical to read the ground line
    // off.
    trees: ["obj.obs_sandstone_pillar", "obj.obs_deadtree", "obj.obs_stalagmite",
      "obj.obs_rock_arch"],
    scrub: ["obj.obs_deadbush", "obj.obs_scree", "obj.obs_tumbleweed", "obj.obs_desert_shrub"],
    furniture: ["obj.obs_scree", "obj.bld_sea_barrel", "obj.obs_milestone", "obj.obs_desert_bones"],
    square: ["obj.obs_woodpile", "obj.obs_broken_cart", "obj.pu_campfire", "obj.obs_scree",
      "obj.obs_fence", "obj.obs_milestone", "obj.obs_tumbleweed", "obj.obs_woodpile"],
  },
  swamp: {
    // No logs in this list, and that is the treeline defect rather than a
    // taste: `obs_swamp_log` and `obs_hollow_log` are HORIZONTAL sprites, and
    // a horizontal sprite planted on the painted hillside has no vertical to
    // tell a viewer where its ground line is - shot at 1080p Fenmire carried
    // four of them lying in the fog band with nothing under them, and a
    // critic read them as zeppelins moored over the swamp. They keep their
    // places in `scrub` and `furniture`, which are drawn near enough that the
    // ground under them is visible. What the back band needs is uprights.
    trees: ["obj.obs_deadtree", "obj.obs_willow", "obj.obs_deadtree", "obj.obs_swamp_shrine"],
    // `obs_swamp_log` is out of the scrub list for the reason it is out of the
    // trees list: the back band draws scrub as well as trees, and one log
    // survived the treeline cut by arriving from here.
    scrub: ["obj.obs_marsh_grass", "obj.obs_nettle_patch", "obj.obs_marsh_grass",
      "obj.obs_deadbush"],
    furniture: ["obj.obs_marsh_grass", "obj.obs_swamp_log", "obj.bld_sea_barrel", "obj.obs_rocks"],
    square: ["obj.obs_swamp_log", "obj.obs_woodpile", "obj.obs_fishing_net", "obj.obs_dock_post",
      "obj.obs_marsh_grass", "obj.obs_rotting_fence", "obj.pu_campfire", "obj.obs_woodpile"],
  },
  desert: {
    // `obs_desert_rock` is out of both lists the back band draws from - the
    // treeline picks from `trees` 55% of the time and from `scrub` the rest -
    // for the reason given on `rough` above. Cogworks drew it at device
    // (1700,240) where it happened to land on ground and got away with it;
    // one seed either way and it is Ironhorde's floating layer cake.
    // `obs_sun_bleached_log` goes with it, and that is the swamp's rule
    // applied here: it is a 128x93 HORIZONTAL sprite with no vertical in it,
    // so on the hillside there is nothing to read its ground line off.
    trees: ["obj.obs_sandstone_pillar", "obj.obs_rock_arch", "obj.obs_stalagmite",
      "obj.obs_deadtree"],
    scrub: ["obj.obs_desert_shrub", "obj.obs_tumbleweed", "obj.obs_deadbush", "obj.obs_scree"],
    furniture: ["obj.obs_milestone", "obj.obs_desert_rock", "obj.bld_sea_barrel", "obj.obs_broken_cart"],
    square: ["obj.bld_well", "obj.obs_broken_cart", "obj.obs_desert_rock", "obj.obs_woodpile",
      "obj.obs_sun_bleached_log", "obj.obs_milestone", "obj.pu_campfire", "obj.obs_desert_shrub"],
  },
  // Seven in the square, not six or nine: the promenade walks its list by
  // threes so a row of fourteen posts sees the whole list before it repeats,
  // and that only holds while three does not divide the length.
  coast: {
    // `obs_palm_cluster` and `obs_mangrove_root` are gone from every coast
    // list, and the reason is measured on the atlas rather than judged: over
    // their own opaque pixels they are 9.8% and 29.9% PURE BLACK, against 0.5
    // to 2.7% for the rest of this country's planting. Both are keying
    // failures - the palm's crown interior quantised to #000 instead of to
    // alpha, so a solid black blob sits between its fronds and its trunks -
    // and at any size over about eighty rows they punch a hole in the frame.
    // Shot at 1080p Saltreach measured 5.1% pure black across the whole painting
    // against Castle's 0.33%.
    trees: ["obj.obs_palm", "obj.obs_bamboo", "obj.obs_palm", "obj.obs_reed_bed"],
    scrub: ["obj.obs_reeds", "obj.obs_coral", "obj.obs_dune", "obj.obs_jungle_fern"],
    furniture: ["obj.obs_dock_post", "obj.bld_sea_barrel", "obj.obs_coral", "obj.obs_sand_ripple"],
    square: ["obj.obs_dock_post", "obj.obs_fishing_net", "obj.bld_sea_barrel",
      "obj.obs_woodpile", "obj.obs_broken_cart", "obj.pu_campfire", "obj.obs_coral"],
  },
});

/**
 * The one thing on the screen that stands in FRONT of the town.
 *
 * Every the classic town screen has something crossing the frame at the viewer's own
 * distance - Edgewater's two swamp trunks run from the top of the painting to
 * the bottom of it and take a hut, a walkway and half a ziggurat with them.
 * Ours had nothing nearer than the front band, so the deepest thing in the
 * frame was a building standing on the ground, and a scene whose nearest plane
 * is its subject reads as an elevation drawing however well the subject is
 * painted.
 *
 * ONE design per country and two instances of it, which is eight sprites, not
 * four hundred - and all eight already ship in the map-object atlas, drawn in
 * the same hand and lit from the same side as the buildings. Nothing new was
 * asked of an artist.
 *
 * `scale` is per design rather than shared because the sources run from 96 to
 * 187 pixels tall and what has to be equal is the DRAWN height: about 250 to
 * 300 canvas pixels, enough to cross the front band and be cut by the frame.
 *
 * That target used to be 380, and 380 is what a critic measured as the second
 * half of the occluder defect: "at 250% the Cogworks cactus steps at ~3 device
 * pixels per texel against the buildings' 1.5 - it is clip-art pasted on a
 * pixel painting", and "the left mesa is the largest object in the frame".
 * The two are one number. A 122-row butte magnified to 380 canvas rows is
 * 3.1x, which is 4.7 device pixels per texel at the 3/2 stage against the
 * front band's 1.5, and it is also 373 canvas pixels wide - a third of the
 * frame, twice over. Cutting the target to 250-300 halves the texel error and
 * takes the pair out of being the two largest silhouettes on the screen in one
 * move, and it costs nothing an occluder is for: the frame still cuts it, it
 * still stands nearer than the front band, it still laps a building.
 *
 * The floor under it is the spec's, not a taste: `town-place.spec.ts` requires
 * both instances to draw over 1.5, so no design here may fall under about 1.6
 * once its socket multiplier is applied.
 */
interface Occluder { readonly art: string; readonly scale: number }

const FOREGROUND: Readonly<Record<GroundKind, Occluder>> = Object.freeze({
  grass: { art: "obj.obs_cypress", scale: 1.62 },
  // Was `obs_pine_cluster`: ONE sprite containing three green summer pines,
  // drawn twice, so a snow town's near ground carried six trees in two
  // mirrored stamps in full summer green while every roof behind them carried
  // snow. `obs_snowpine` is one snow-laden pine - the right material and one
  // silhouette instead of three.
  snow: { art: "obj.obs_snowpine", scale: 2.55 },
  ash: { art: "obj.obs_cinder_cone", scale: 2.05 },
  // Was obs_cave_column, a 64x143 stone cylinder, and a blind critic named it
  // the weakest asset in the whole set: "a flat untextured cylinder with a
  // plain elliptical cap, no chroma, no rim light". No filter answers that,
  // because the sprite is grey and saturate() scales a chroma of zero.
  // `obs_crystal_formation` is 64x100 of angular violet spires - the colour
  // the rest of Deepvault is built on, and a faceted silhouette instead of a
  // tube.
  cave: { art: "obj.obs_crystal_formation", scale: 2.55 },
  grave: { art: "obj.obs_standing_stone", scale: 1.75 },
  // Was `obs_mesa_rock`, and a critic named it without being asked: "flat
  // cream top with no value gradient across 500px, sides in evenly-spaced
  // horizontal candy stripes with a repeating scallop, hard clean base line
  // straight onto the plaza - it reads as a sponge cake". That is the SPRITE,
  // not the light on it: a butte drawn as horizontal bands and capped with
  // one flat tone has no value break anywhere in it, and nothing a filter
  // does to a flat plane makes it not flat.
  //
  // `obs_rock_arch` is the same quarry - warm banded sandstone, R-B +54 over
  // its opaque pixels, which is why the neutral granite this table used to
  // carry was rejected - and it is the one large obstacle in the atlas whose
  // silhouette has a HOLE in it. An occluder with a hole frames the town
  // through itself instead of blanking a slice of it, and its cross-section
  // is an arch rather than a slab, so its own form carries the value break
  // the mesa's top plane never had. It is also 0.06% pure black against the
  // mesa's 0.7%.
  rough: { art: "obj.obs_rock_arch", scale: 1.90 },
  swamp: { art: "obj.obs_ivy_trunk", scale: 1.70 },
  // Was `obs_cactus_cluster`, and a critic took it apart in three measured
  // pieces after a round in which it was supposed to have been fixed: "(a) a
  // 6-8px near-neutral dark keyline against tan sky. (b) smooth vertical
  // gradients on every rib with zero dithering, against buildings that are
  // dithered everywhere. (c) the body reads (78,103,51) to (99,123,43) - the
  // only saturated green in a scene whose entire palette is dusty red-brown,
  // so the eye goes to the cactus before it goes to the Sun temple." All
  // three are in the SPRITE. It is airbrushed clip-art with a hard black
  // outline drawn at 1x, and magnified 1.8 times that outline is a two-pixel
  // rule around the largest object in the frame; no filter chain removes an
  // outline, and the black lift below only turns it grey.
  //
  // `obs_sandstone_pillar` is the same quarry as `rough`'s arch and measures
  // the same way - (113,81,50), R-B +63 against the cactus' +11, 0.73% pure
  // black - and it is drawn the way the buildings are: stacked courses with a
  // dithered face on each, no outline, no gradient. A stack of eroded blocks
  // is also a NARROW silhouette, 64 source pixels against the cactus' 128, so
  // at the frame's edge it screens a column of the picture rather than a
  // third of it.
  desert: { art: "obj.obs_sandstone_pillar", scale: 2.00 },
  // Was `obs_palm_cluster` and the swap is measured, not judged: that sprite
  // is 9.8% pure black over its own opaque pixels, and the black is not
  // shading - it is the crown's interior, a keying failure that quantised the
  // background between the fronds and the trunks to #000 instead of to alpha.
  // Magnified to a foreground occluder it punched a 400px hole through the
  // middle of Saltreach. `obs_bamboo` is 1.7% black, it is the same country, and a
  // stand of canes is a vertical screen rather than a mass - which is what an
  // occluder at the frame's edge is for.
  coast: { art: "obj.obs_bamboo", scale: 2.35 },
});

/**
 * Below the canvas, so the occluder is CUT by the frame rather than standing on
 * a floor the player can see the bottom of. A tree whose roots are visible is a
 * tree at a measurable distance; one that leaves the frame is in the room.
 */
const FOREGROUND_Y = 752;

/**
 * Where THIS town's two foreground occluders stand, and how big they are.
 *
 * BOTH of them are at a frame edge now, and that is the round's largest change
 * to this file. The larger one used to stand IN the front-band joint its
 * socket names, and a critic put the result plainly: "a foreground occluder
 * frames, it does not censor - on Cogworks and Saltreach the pair sits mid-frame
 * over the buildings". He is right, and no choice of joint fixes it: every
 * joint in the front band is over a building, because the front band laps by
 * construction. The same finding arrived from the other side on Gravewatch,
 * where moving the menhir off centre dropped it onto the best silhouette in
 * the frame - "centre was bad; covering your best building is not obviously
 * better".
 *
 * `joint` still decides the placement and it is still the reason no two towns
 * are alike, but what it now decides is WHICH EDGE and HOW FAR IN. The
 * occluder goes to the frame edge its joint is nearest, and how far in it
 * stands is how far that joint sits from that edge, compressed to a twelfth -
 * so a town socketed on joint 0 stands hard against the left edge and one
 * socketed on joint 2 stands a little further into the picture. `edge` is the
 * OTHER instance's inset, on the opposite side. An occluder at a frame edge is
 * cut twice, by the bottom and by the side, so more of it is out of the
 * picture than in it, which is what a near plane looks like - and the thing it
 * laps is whatever the layout put in the outermost slot rather than the
 * subject of the composition.
 *
 * `scale` multiplies the design's own and may not take any design under 1.5,
 * which is what `town-place.spec.ts` pins. `y` decides how deep the frame cuts
 * the thing: every one of these has its ground line below the canvas on
 * purpose - an object whose roots you can see is at a measurable distance, one
 * that leaves the frame is in the room - and how much of it leaves the frame
 * is the only depth cue an object with no visible base has.
 */
interface Socket {
  /** Which front-band joint, left to right, counted from 0. */
  readonly joint: number;
  /** Multiplies the country design's own scale. */
  readonly scale: number;
  /** Ground line of the larger of the pair; the canvas ends at 720. */
  readonly y: number;
  /** Which frame edge the SMALLER one hangs off, and how far in. */
  readonly edge: number;
}

/**
 * Every `scale` here is at or above the ratio that keeps its country's design
 * over 1.5 once multiplied, because `town-place.spec.ts` will not accept an
 * occluder under it - which is why the four sockets that used to sit at 0.86
 * to 0.92 have all come up. The designs themselves came DOWN by more than that
 * in `FOREGROUND`, so the pair is smaller than it was in every town.
 */
const SOCKET: Readonly<Record<string, Socket>> = Object.freeze({
  sunspire: { joint: 1, scale: 1.00, y: 752, edge: 46 },
  greenmantle: { joint: 3, scale: 1.04, y: 778, edge: 62 },
  skyreach: { joint: 0, scale: 1.04, y: 746, edge: 34 },
  emberpit: { joint: 4, scale: 0.88, y: 764, edge: 70 },
  gravewatch: { joint: 4, scale: 0.96, y: 786, edge: 40 },
  deepvault: { joint: 3, scale: 0.86, y: 774, edge: 78 },
  ironhorde: { joint: 0, scale: 1.00, y: 742, edge: 52 },
  fenmire: { joint: 1, scale: 1.02, y: 772, edge: 30 },
  prismgate: { joint: 4, scale: 1.06, y: 758, edge: 66 },
  saltreach: { joint: 2, scale: 1.02, y: 748, edge: 44 },
  cogworks: { joint: 3, scale: 1.04, y: 782, edge: 56 },
  frostmark: { joint: 1, scale: 1.10, y: 740, edge: 38 },
  // The thirteenth. Its own joint, scale and ground line like the other
  // twelve - the whole point of the table is that no two towns share one.
  blackwater: { joint: 0, scale: 0.98, y: 768, edge: 58 },
});

const SOCKET_DEFAULT: Socket = Object.freeze({ joint: 2, scale: 1, y: FOREGROUND_Y, edge: 46 });

/**
 * The shortest the SECOND occluder is allowed to be drawn.
 *
 * The pair used to be one design at one size mirrored about the frame's centre
 * line, which is what made it read as a stamp applied twice, so the smaller of
 * the two is drawn at 0.80-0.94 of the larger and flipped on its own. That
 * shrink cannot be a plain factor: a foreground occluder stops being one the
 * moment it no longer crosses the front band, and `town-place.spec.ts` will
 * not accept either instance under 1.5. So it is a floor, and it bites on the
 * designs that have spare height.
 */
const FG_MIN_SCALE = 1.56;

export const socket = (faction: string): Socket => SOCKET[faction] ?? SOCKET_DEFAULT;

interface Span { readonly from: number; readonly to: number; readonly y: number }

/** The lawn a band leaves uncovered, as spans between drawn footprints. */
function openSpans(row: readonly TownSlot[]): readonly Span[] {
  const sorted = [...row].sort((a, b) => a.x - b.x);
  const out: Span[] = [];
  let cursor = 0;
  let y = sorted[0]?.y ?? 0;
  for (const s of sorted) {
    const left = s.x - drawnWidth(s) / 2;
    if (left - cursor > 0) out.push({ from: cursor, to: left, y });
    cursor = s.x + drawnWidth(s) / 2;
    y = s.y;
  }
  if (TOWN_CANVAS_W - cursor > 0) out.push({ from: cursor, to: TOWN_CANVAS_W, y });
  return out;
}

/** How many props a span of this width should carry, and how far apart. */
const SPACING = 56;

/**
 * The shallowest ground line a back-band prop may be planted on.
 *
 * 286 is where `.hs-town__seam` starts - the row the painted backdrop's hills
 * stop on and the flat ground plane begins - and a prop whose ground line is
 * above it is standing on painted hillside a quarter of a mile away, drawn at
 * the size of a thing forty feet off. The treeline used to be lifted up to
 * forty rows off its band's floor to sit behind it, which put Fenmire' back
 * band at canvas rows 253-290, and a critic read the result exactly as it is:
 * "three giant logs float in the sky above the horizon... at 180% they read as
 * zeppelins moored over the swamp". Nothing that hangs in the fog band can be
 * rescued by a softer shadow - a faint shadow on a floating object only
 * removes the last cue that it is floating.
 *
 * Fourteen rows of clearance below the seam, which is enough that the prop's
 * own contact shadow lands on the ground plane rather than across the join.
 */
const BACK_PROP_FLOOR = 300;

/** Where the near promenade's furniture stands, and how much of it there is. */
const PROMENADE_Y = 692;
const PROMENADE_POSTS = 14;

/**
 * The country's planting list with its own foreground occluder taken out.
 *
 * One design per country and two instances of it is the rule the occluders
 * are built on, and it holds only if the design is not ALSO scattered through
 * the middle distance. Snow broke it: `FOREGROUND.snow` is
 * `obs_pine_cluster` and `FLORA.snow.trees` opens with `obs_pine_cluster`,
 * so Tower and Frostmark drew the same conifer as the two cut-off giants in
 * front AND as trees behind the town - four instances of one silhouette
 * spread evenly across the frame, which reads as a hedge rather than as a
 * near plane. `rough` acquired the same collision the moment its occluder
 * became `obs_mesa_rock`, which is the first entry in `FLORA.rough.trees`.
 *
 * `square` is deliberately not filtered. The promenade walks its list by
 * threes because three is coprime with every square list's length, so
 * shortening one could make the walk repeat a prop every third post; no
 * square list names its country's occluder today, and a test pins that.
 */
function plantable(kind: GroundKind): Flora {
  const f = FLORA[kind];
  const own = FOREGROUND[kind].art;
  const without = (list: readonly string[]): readonly string[] => {
    const kept = list.filter((art) => art !== own);
    return kept.length === 0 ? list : kept;
  };
  return {
    trees: without(f.trees),
    scrub: without(f.scrub),
    furniture: without(f.furniture),
    square: f.square,
  };
}

/**
 * The two tables a test needs to check that a country never plants its own
 * foreground occluder. Exported rather than duplicated in the spec, because a
 * copy of a table is the one thing a table test cannot catch drifting.
 */
export const FOREGROUND_FOR_TEST: Readonly<Record<GroundKind, Occluder>> = FOREGROUND;
export const FLORA_FOR_TEST = (kind: GroundKind): Flora => plantable(kind);

/**
 * Everything standing on the ground that is not a building.
 *
 * Trees go behind the back band and into its gaps - they are what stops the
 * skyline being five towers on an empty lawn - and the near band gets street
 * furniture along the promenade, where the player's eye actually rests.
 */
export function townScenery(
  layout: readonly TownSlot[], faction: string,
): readonly Prop[] {
  const out: Prop[] = [];
  const kind = groundKind(faction);
  const flora = plantable(kind);
  const back = layout.filter((s) => s.band === "back");
  if (back.length === 0) return out;

  // Behind the back band: a treeline on the far side of the lane, planted
  // thickest where the band leaves the most sky.
  for (const span of openSpans(back)) {
    const width = span.to - span.from;
    if (width < 44) continue;
    const n = Math.max(1, Math.floor(width / SPACING));
    for (let i = 0; i < n; i++) {
      const t = (i + 0.5) / n;
      const seed = Math.round(span.from * 13 + t * 977);
      const x = span.from + width * t + (hash01(seed + 7) - 0.5) * 26;
      const deep = hash01(seed + 31) < 0.55;
      const art = pick(deep ? flora.trees : flora.scrub, seed);
      out.push({
        art,
        x: Math.round(x),
        y: Math.max(
          BACK_PROP_FLOOR,
          Math.round(span.y - (deep ? 10 : 2) - hash01(seed + 3) * 12),
        ),
        scale: BAND_SCALE.back * (deep ? 1.15 + hash01(seed + 11) * 0.42 : 0.9),
        band: "back",
        flip: hash01(seed + 5) < 0.5,
        fg: false,
        lit: isLit(art),
      });
    }
  }

  // Between the middle band's buildings: low scrub, one clump per joint.
  //
  // Placed on the joint rather than on a measured gap, because the gap the eye
  // sees is not the gap the boxes describe - building art carries a
  // transparent margin, so two footprints that overlap can still show a strip
  // of lawn between the masonry. A bush on every joint covers it either way,
  // and it is drawn just in front of both so it laps over their bases.
  const mid = layout.filter((s) => s.band === "middle").sort((a, b) => a.x - b.x);
  for (let i = 1; i < mid.length; i++) {
    const left = mid[i - 1]!;
    const right = mid[i]!;
    const seed = Math.round(left.x * 29 + right.x);
    const art = pick(flora.scrub, seed);
    out.push({
      art,
      x: Math.round((left.x + drawnWidth(left) / 2 + right.x - drawnWidth(right) / 2) / 2
        + (hash01(seed + 2) - 0.5) * 26),
      y: Math.round(Math.max(left.y, right.y) + 6 + hash01(seed + 8) * 10),
      scale: BAND_SCALE.middle * (0.72 + hash01(seed + 5) * 0.22),
      band: "middle",
      flip: hash01(seed + 9) < 0.5,
      fg: false,
      lit: isLit(art),
    });
  }

  // Between the front band's buildings: the same trick as the middle band.
  // The near row nearly touches now, so the gaps are small - but "small" is
  // exactly the size of the bare green wedge that shows between two aprons,
  // and a wedge of lawn pinched between two paved yards reads as a hole the
  // layout failed to fill rather than as a garden.
  const front = layout.filter((s) => s.band === "front").sort((a, b) => a.x - b.x);
  for (let i = 1; i < front.length; i++) {
    const left = front[i - 1]!;
    const right = front[i]!;
    const seed = Math.round(left.x * 41 + right.x);
    const art = pick(flora.scrub, seed);
    out.push({
      art,
      x: Math.round((left.x + drawnWidth(left) / 2 + right.x - drawnWidth(right) / 2) / 2
        + (hash01(seed + 2) - 0.5) * 22),
      y: Math.round(Math.min(left.y, right.y) - 26 - hash01(seed + 8) * 16),
      scale: 0.52 + hash01(seed + 5) * 0.16,
      band: "front",
      flip: hash01(seed + 9) < 0.5,
      fg: false,
      lit: isLit(art),
    });
  }

  // The promenade itself. Spread across the whole width rather than parked at
  // the two ends, kept clear of the doorways the approaches come out of, and
  // never two of the same thing in a row - a line of identical signposts is
  // scattered junk, not a square.
  //
  // Every one of those inputs used to be a constant, so the square was laid
  // out identically in all twelve towns and only the art changed - which is
  // the other half of what the critic saw around the occluder: "the same well,
  // the same bench, the same flower clump and the same log pile in the same
  // relative positions". The town's own name is folded into the seed and into
  // where the walk through its square list starts, so the jitter, the sizes
  // and the order are its own.
  const doors = front.map((f) => f.x);
  const tone = Math.round(hash01(nameSeed(faction)) * 4096);
  for (let i = 0; i < PROMENADE_POSTS; i++) {
    const t = (i + 0.5) / PROMENADE_POSTS;
    const seed = Math.round(t * 7919 + 31) + tone;
    const x = Math.round(18 + (TOWN_CANVAS_W - 36) * t + (hash01(seed) - 0.5) * 44);
    if (doors.some((d) => Math.abs(d - x) < 34)) continue;
    // Stepped by three rather than hashed: three is coprime with every
    // square list, so the row walks the whole list before it repeats
    // anything. A hash gave three broken carts in fourteen posts. Where the
    // walk STARTS is the town's own.
    const list = flora.square;
    const k = (i * 3 + tone) % list.length;
    const art = list[k]!;
    out.push({
      art,
      x,
      y: Math.round(PROMENADE_Y + hash01(seed + 13) * 22),
      scale: 0.48 + hash01(seed + 17) * 0.18,
      band: "front",
      flip: hash01(seed + 4) < 0.5,
      fg: false,
      lit: isLit(art),
    });
  }

  // The two foreground occluders, last in the list and last in the painter's
  // sort because their ground line is below the canvas.
  //
  // One at each frame edge. Not one in a front-band joint and one at an edge,
  // which is what the shipped round did: every joint in the front band is over
  // a building, because the front band laps by construction, so there is no
  // joint that hides nothing. The socket's joint is still what places them -
  // see SOCKET - but what it now chooses is the edge and the inset.
  //
  // Both flips and the smaller one's size come from the town's own seed rather
  // than from which side of the frame it is on. Mirroring one design about the
  // frame's centre line is what made the pair read as a stamp applied twice:
  // two silhouettes the same size, the same way up, and exactly each other's
  // reflection. Drawn at 0.80 to 0.94 of the larger and flipped on its own,
  // the smaller one is another of the species instead.
  const fg = FOREGROUND[groundKind(faction)];
  const sock = socket(faction);
  const joints: number[] = [];
  for (let i = 1; i < front.length; i++) {
    const a = front[i - 1]!;
    const b = front[i]!;
    joints.push((a.x + drawnWidth(a) / 2 + b.x - drawnWidth(b) / 2) / 2);
  }
  const joint = joints.length === 0
    ? TOWN_CANVAS_W / 2
    : joints[Math.min(joints.length - 1, sock.joint)]!;
  // Which edge the joint is nearest, and how far in from it. The twelfth is
  // what keeps a joint two thirds of the way across the frame from putting an
  // occluder two thirds of the way across the frame: the whole point is that
  // it ends up at the edge, and the joint decides which one and by how little.
  const toLeft = joint < TOWN_CANVAS_W / 2;
  const inset = 34 + Math.round(Math.min(joint, TOWN_CANVAS_W - joint) / 12);
  const seed = nameSeed(faction);
  const bigScale = fg.scale * sock.scale;
  const bigFlip = hash01(seed + 101) < 0.5;
  out.push({
    art: fg.art,
    x: toLeft ? inset : TOWN_CANVAS_W - inset,
    y: sock.y,
    scale: bigScale,
    band: "front",
    flip: bigFlip,
    fg: true,
    lit: false,
  });
  out.push({
    art: fg.art,
    x: toLeft ? TOWN_CANVAS_W - sock.edge : sock.edge,
    y: sock.y - 18,
    scale: Math.max(FG_MIN_SCALE, bigScale * (0.70 + hash01(seed + 211) * 0.20)),
    band: "front",
    // The SAME flip as the larger one, and that is a lighting rule rather
    // than a composition one. Every sprite in the atlas is painted lit from
    // the upper left; mirroring one puts its highlight on the shade side, and
    // two instances mirrored about the frame's centre line put two objects in
    // one picture lit by two different suns. It is also what a critic
    // measured - "Ironhorde's two rock arches are literal mirror twins,
    // mirror-correlation 0.70 between x 0-140 and the flipped x 1780-1920" -
    // because a mirror is exactly what that number detects. Two instances of
    // one design at 0.70 to 0.90 of each other's size, lit the same way, with
    // their own talus at their feet, is as far as this can go while
    // `town-place.spec` and `town-aerial.spec` both pin the pair at one
    // design and two instances.
    flip: bigFlip,
    fg: true,
    lit: false,
  });

  // Talus at each occluder's foot.
  //
  // "Their feet are below the canvas" answers only half of what the frame
  // shows. Ironhorde's arch measures a hard straight line where its legs
  // meet the diamond paving at device (1690,880)-(1900,905) with no scree, no
  // dust drift and no darkening, because a sprite trimmed to its own alpha
  // box ends where its art ends whatever row the canvas is cut on - so
  // between the last painted pixel of the leg and the bottom of the frame
  // there is clean plaza. Two small stones per foot from the country's own
  // scrub list, standing ON the ground plane a few rows above the cut, put
  // rubble in that gap - and because they are ordinary props rather than
  // occluders they take the shadow layer's cast, pools and seat, which is the
  // contact darkening the same finding asks for.
  //
  // They are also the only near-plane objects in the frame that are NOT the
  // occluder design: the pair is one design twice by rule (`town-place.spec`
  // and `town-aerial.spec` both pin the count), so what can be varied at the
  // frame's edge is what stands around it.
  const talusFoot = [
    { x: toLeft ? inset : TOWN_CANVAS_W - inset, w: 64 * bigScale },
    { x: toLeft ? TOWN_CANVAS_W - sock.edge : sock.edge, w: 58 * bigScale },
  ];
  for (let f = 0; f < talusFoot.length; f++) {
    const foot = talusFoot[f]!;
    for (let i = 0; i < 2; i++) {
      const s2 = seed + f * 613 + i * 97;
      // Offset to the side the frame does not cut, so the stone lands in the
      // picture rather than half outside it.
      const side = foot.x < TOWN_CANVAS_W / 2 ? 1 : -1;
      const x = Math.round(foot.x + side * (foot.w * (0.22 + hash01(s2) * 0.34)));
      if (x < 6 || x > TOWN_CANVAS_W - 6) continue;
      const art = pick(flora.scrub, s2 + 5);
      out.push({
        art,
        x,
        y: Math.round(TOWN_CANVAS_H - 4 - hash01(s2 + 11) * 16),
        scale: 0.44 + hash01(s2 + 23) * 0.30,
        band: "front",
        flip: hash01(s2 + 41) < 0.5,
        fg: false,
        lit: isLit(art),
      });
    }
  }

  return out;
}
